import React, { useEffect, useRef, useState } from 'react'
import CompetitorSelect from '../../CompetitorSelect'
import CompetitionSelect, { CompetitionFilter } from '../../CompetitionSelect'
import SeasonSelect from '../../SeasonSelect'
import { api } from '../../../services/api'
import { Competitor, Season } from '../../../types/forms'

// All carousel lookup filters speak ID arrays (the wire contract in
// metadata.filters). The reused typeaheads work in rich objects, so each
// adapter keeps a session label cache: items the user picks are cached, and IDs
// loaded from a saved config are batch-resolved to their real labels on mount
// (via the list endpoints' `ids` param) so editing an existing widget shows
// friendly names instead of raw UUIDs.
export interface LookupProps {
  ids: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
}

// Every catalog typeahead we wrap keys its options by tc_id and is multi-select.
// makeLookupAdapter bridges one to the ID-array LookupProps contract: it maps
// the stored IDs to cached rich objects (falling back to a raw-ID stub until
// resolved), caches whatever the user picks, and batch-resolves any uncached
// IDs on mount so saved filters rehydrate their labels.
const makeLookupAdapter = <T extends { tc_id: string }>(
  Component: React.ComponentType<any>,
  fallback: (id: string) => T,
  resolve: (ids: string[]) => Promise<T[]>,
  defaultPlaceholder: string,
  extraProps: Record<string, unknown> = {},
): React.FC<LookupProps> => {
  const Adapter: React.FC<LookupProps> = ({ ids, onChange, placeholder }) => {
    const cache = useRef<Map<string, T>>(new Map())
    const [, forceRender] = useState(0)

    // Rehydrate labels for any stored IDs the user hasn't picked this session.
    useEffect(() => {
      const missing = ids.filter(id => !cache.current.has(id))
      if (missing.length === 0) return
      let cancelled = false
      resolve(missing)
        .then(items => {
          if (cancelled) return
          items.forEach(o => cache.current.set(o.tc_id, o))
          forceRender(n => n + 1)
        })
        .catch(() => {})
      return () => {
        cancelled = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids.join(',')])

    const value: T[] = ids.map(id => cache.current.get(id) ?? fallback(id))
    return (
      <Component
        isMulti
        placeholder={placeholder ?? defaultPlaceholder}
        value={value}
        onChange={(v: T[] | null) => {
          const arr = v ?? []
          arr.forEach(o => cache.current.set(o.tc_id, o))
          onChange(arr.map(o => o.tc_id))
        }}
        {...extraProps}
      />
    )
  }
  return Adapter
}

export const PlayerFilterSelect = makeLookupAdapter<Competitor>(
  CompetitorSelect,
  (id) => ({ tc_id: id, ext_id: '', name: id }),
  async (ids) => {
    const resp = await api.get('/competitors', { ids: ids.join(','), limit: 100 })
    return (resp?.competitors || []).map((c: any) => ({
      tc_id: c.tc_competitor_id,
      ext_id: c.ext_competitor_id ?? '',
      name: c.tc_name || c.ext_name || c.tc_competitor_id,
      team_type: c.tc_team_type || c.ext_team_type || 'singles',
    }))
  },
  'Search players…',
  { teamType: 'mixed' },
)

export const CompetitionFilterSelect = makeLookupAdapter<CompetitionFilter>(
  CompetitionSelect,
  (id) => ({ tc_id: id, name: id }),
  async (ids) => {
    const resp = await api.get('/competitions', { ids: ids.join(','), limit: 100 })
    return (resp?.competitions || []).map((c: any) => ({
      tc_id: c.tc_competition_parent_id,
      name: c.tc_name || c.ext_name || c.tc_competition_parent_id,
    }))
  },
  'Search tournaments…',
)

export const SeasonFilterSelect = makeLookupAdapter<Season>(
  SeasonSelect,
  (id) => ({ tc_id: id, ext_id: '', name: id, year: 0 }),
  async (ids) => {
    const resp = await api.get('/seasons', { ids: ids.join(','), limit: 100 })
    return (resp?.seasons || []).map((s: any) => ({
      tc_id: s.tc_season_id,
      ext_id: s.ext_season_id ?? '',
      name: s.tc_name || s.ext_name || s.tc_season_id,
      year: s.tc_year || s.ext_year || 0,
    }))
  },
  'Search seasons…',
)
